import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch, BASE_URL } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import MapaLocalizacao from "../components/ui/MapaLocalizacao";

const TIPO_LABEL = {
  entrada: "Entrada",
  saida: "Saída",
  intervalo_inicio: "Saída Intervalo",
  intervalo_fim: "Volta Intervalo",
};

function dataHoraLocal(iso) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function linha(label, valor) {
  return (
    <div className="comprovante-linha" style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ fontSize: 12.5, color: COLORS.textMid, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: COLORS.text, fontWeight: 700, textAlign: "right" }}>{valor}</span>
    </div>
  );
}

export default function Comprovante() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, usuario } = useAuth();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(true);
  const [fotoUrl, setFotoUrl] = useState(null);
  const [fotoCarregando, setFotoCarregando] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [erroPdf, setErroPdf] = useState("");

  const baixarPdfAssinado = async () => {
    setBaixandoPdf(true);
    setErroPdf("");
    try {
      const res = await fetch(`${BASE_URL}/api/ponto/registros/${id}/comprovante/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      setErroPdf("Não foi possível gerar o PDF assinado agora.");
    } finally {
      setBaixandoPdf(false);
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const data = await apiFetch("GET", `/api/ponto/registros/${id}/comprovante`, undefined, token);
      setDados(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { carregar(); }, [carregar]);

  // `fotoCarregando` trava o botão de imprimir enquanto a foto ainda não
  // chegou — sem isso, clicar em "Imprimir / PDF" rápido (a busca da foto
  // roda em segundo plano, depois que o resto do comprovante já carregou)
  // gerava um PDF sem a selfie, mesmo o registro tendo foto de verdade.
  useEffect(() => {
    if (!dados?.tem_foto) return;
    let cancelado = false;
    setFotoCarregando(true);
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/ponto/foto/${dados.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        if (!cancelado) setFotoUrl(URL.createObjectURL(blob));
      } catch {
        // sem foto pra mostrar não impede o resto do comprovante
      } finally {
        if (!cancelado) setFotoCarregando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [dados, token]);

  // Sempre volta pra tela de origem de verdade (Folha de Ponto daquele
  // funcionário, ou Meu Histórico pra quem vê o próprio comprovante) em vez
  // de `navigate(-1)` — o histórico do navegador nem sempre tem essa tela
  // como passo anterior (ex: comprovante aberto a partir de outro lugar), o
  // que fazia "Voltar" cair num lugar errado.
  const voltar = () => {
    if (usuario?.perfil === "funcionario") {
      navigate("/meu-historico");
    } else if (dados?.funcionario_id) {
      navigate(`/funcionarios/historico?funcionario_id=${dados.funcionario_id}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) return <EstadoCarregando texto="Carregando comprovante..." />;

  if (erro || !dados) {
    return (
      <Card>
        <p style={{ color: COLORS.danger, fontSize: 14, margin: 0 }}>{erro || "Comprovante não encontrado."}</p>
        <Btn variant="secondary" onClick={voltar} style={{ marginTop: 16 }}>Voltar</Btn>
      </Card>
    );
  }

  const temLocalizacao = dados.latitude != null && dados.longitude != null;
  const manual = dados.origem === "manual";
  const automatico = dados.origem === "auto";

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      {/* Sem isso o comprovante inteiro (mapa em tamanho de tela + selfie
          grande + espaçamento pensado pra leitura na tela) transbordava pra
          uma segunda folha impressa — o conteúdo em si cabe numa folha só,
          só precisa ser mais compacto no papel do que é na tela. */}
      <style>{`
        @media print {
          .comprovante-card { padding: 20px !important; }
          .comprovante-header { margin-bottom: 16px !important; padding-bottom: 14px !important; }
          .comprovante-nome-row { margin-bottom: 12px !important; }
          .comprovante-alerta { margin-bottom: 12px !important; padding: 10px !important; }
          .comprovante-linha { padding: 7px 0 !important; }
          .comprovante-map-wrap { padding: 12px 0 !important; }
          .comprovante-justificativa { margin-top: 12px !important; padding: 10px !important; }
          .comprovante-foto { margin-top: 14px !important; }
          .comprovante-foto-img { max-width: 170px !important; }
          .comprovante-codigo { margin-top: 14px !important; padding: 10px !important; }
          .comprovante-hash { font-size: 11.5px !important; line-height: 1.5 !important; }
          .comprovante-codigo-explicacao { font-size: 10px !important; line-height: 1.4 !important; }
          .comprovante-footer { margin-top: 18px !important; padding-top: 12px !important; }
          .tela-only { display: none !important; }
          .comprovante-print-lado-a-lado { display: flex !important; gap: 20px; align-items: flex-start; justify-content: center; }
        }
        .comprovante-print-lado-a-lado { display: none; }
      `}</style>

      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 8 }}>
        <button
          onClick={voltar}
          style={{ background: "none", border: "none", color: COLORS.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}
        >
          <Icon name="arrowLeft" size={15} color={COLORS.textMid} /> Voltar
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {erroPdf && <span style={{ fontSize: 12, color: COLORS.danger }}>{erroPdf}</span>}
          <Btn variant="secondary" onClick={baixarPdfAssinado} disabled={baixandoPdf}>
            {baixandoPdf ? "Gerando..." : "PDF assinado"}
          </Btn>
          <Btn onClick={() => window.print()} disabled={fotoCarregando}>
            {fotoCarregando ? "Carregando foto..." : "Imprimir / PDF"}
          </Btn>
        </div>
      </div>

      <Card className="comprovante-card" style={{ padding: 32 }}>
        <div className="comprovante-header" style={{ textAlign: "center", marginBottom: 24, paddingBottom: 20, borderBottom: `2px solid ${COLORS.text}` }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>
            Comprovante de Registro de Ponto
          </p>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            {dados.empresa_nome}
          </h1>
          {dados.empresa_cnpj && (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>CNPJ: {dados.empresa_cnpj}</p>
          )}
        </div>

        <div className="comprovante-nome-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{dados.funcionario_nome}</div>
            <div style={{ fontSize: 12.5, color: COLORS.textMid }}>
              {[dados.funcionario_cargo, dados.funcionario_cpf ? `CPF: ${dados.funcionario_cpf}` : null].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          <Badge tone={manual || automatico ? "warning" : "success"}>{TIPO_LABEL[dados.tipo] || dados.tipo}</Badge>
        </div>

        {automatico && (
          <div className="comprovante-alerta" style={{ marginBottom: 16, padding: 14, background: COLORS.warningBg, border: `1px solid ${COLORS.warning}`, borderRadius: RADIUS.md }}>
            <p style={{ fontSize: 13, color: COLORS.text, margin: 0, fontWeight: 600 }}>
              ⚠ Fechamento automático — a pessoa não bateu essa saída. O sistema fechou o turno sozinho, 30 minutos depois do horário configurado (função que não existe mais para batidas novas — esse registro é de um turno antigo).
            </p>
          </div>
        )}

        {manual && (
          <div className="comprovante-alerta" style={{ marginBottom: 16, padding: 14, background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: RADIUS.md }}>
            <p style={{ fontSize: 13, color: COLORS.text, margin: 0, fontWeight: 600 }}>
              ✎ Lançamento manual — quem registrou essa batida foi{" "}
              {dados.justificativa_aprovado_por_nome ? <strong>{dados.justificativa_aprovado_por_nome}</strong> : "o gestor"}, não a própria pessoa confirmando a presença.
            </p>
          </div>
        )}

        {dados.anulado_em && (
          <div className="comprovante-alerta" style={{ marginBottom: 16, padding: 14, background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}`, borderRadius: RADIUS.md }}>
            <p style={{ fontSize: 13, color: COLORS.text, margin: 0, fontWeight: 600 }}>
              ⚠ Registro anulado em {dataHoraLocal(dados.anulado_em)} por {dados.anulado_por_nome || "-"}. Motivo: {dados.anulado_motivo || "-"}
            </p>
          </div>
        )}
        {!!dados.fora_do_posto && (
          <div className="comprovante-alerta" style={{ marginBottom: 16, padding: 14, background: COLORS.warningBg, border: `1px solid ${COLORS.warning}`, borderRadius: RADIUS.md }}>
            <p style={{ fontSize: 13, color: COLORS.text, margin: 0, fontWeight: 600 }}>
              ⚠ Batida registrada fora do raio dos postos autorizados do funcionário.
            </p>
          </div>
        )}

        {linha("Data e hora do registro", dataHoraLocal(dados.registrado_em))}
        {linha("Tipo de batida", TIPO_LABEL[dados.tipo] || dados.tipo)}
        {linha("Origem", automatico ? "Fechamento automático (o sistema bateu sozinho)" : manual ? `Lançamento manual, por ${dados.justificativa_aprovado_por_nome || "gestor"}` : "Feito no aplicativo")}
        {linha("Posto vinculado", dados.posto_nome || "Nenhum posto vinculado")}
        {dados.posto_endereco && linha("Endereço do posto", dados.posto_endereco)}
        {linha(
          "Localização capturada",
          temLocalizacao
            ? `${Number(dados.latitude).toFixed(6)}, ${Number(dados.longitude).toFixed(6)}${dados.distancia_metros != null ? ` (${dados.distancia_metros}m do posto)` : ""}`
            : "Não capturada"
        )}
        {temLocalizacao && (
          <div className="comprovante-map-wrap" style={{ padding: "16px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <div className="tela-only">
              <MapaLocalizacao latitude={Number(dados.latitude)} longitude={Number(dados.longitude)} tamanho={220} />
            </div>
            {/* Versão à parte, menor, só pra impressão — o mapa monta a
                imagem juntando tiles numa escala calculada a partir do
                tamanho pedido, então não dá pra só encolher via CSS a
                versão de tela (cortaria o mapa em vez de reduzir; teria que
                recalcular a escala, que já é interna ao componente). Na
                impressão fica lado a lado com a selfie (quando tem uma), pra
                economizar altura de página — por isso a foto "solo" mais
                abaixo se esconde na impressão nesse caso (ver `tela-only`
                condicional no bloco da foto).*/}
            <div className="comprovante-print-lado-a-lado">
              <MapaLocalizacao latitude={Number(dados.latitude)} longitude={Number(dados.longitude)} tamanho={170} />
              {fotoUrl && (
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 6px" }}>
                    Selfie
                  </p>
                  <img src={fotoUrl} alt="Selfie da batida" style={{ maxWidth: 170, borderRadius: RADIUS.md, border: `1px solid ${COLORS.border}` }} />
                </div>
              )}
            </div>
          </div>
        )}
        {linha("ID do registro", `#${dados.id}`)}

        {manual && dados.justificativa_texto && (
          <div className="comprovante-justificativa" style={{ marginTop: 16, padding: 14, background: COLORS.gray, borderRadius: RADIUS.md }}>
            <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 6px" }}>
              Justificativa
            </p>
            <p style={{ fontSize: 13, color: COLORS.text, margin: "0 0 8px" }}>"{dados.justificativa_texto}"</p>
            {dados.justificativa_aprovado_por_nome && (
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: 0 }}>
                Aprovado por {dados.justificativa_aprovado_por_nome}
                {dados.justificativa_aprovado_em ? ` em ${dataHoraLocal(dados.justificativa_aprovado_em)}` : ""}
              </p>
            )}
          </div>
        )}

        {fotoUrl && (
          // Quando tem localização, a selfie já aparece lado a lado com o
          // mapa (ver comprovante-print-lado-a-lado acima) — some daqui na
          // impressão pra não duplicar. Sem localização, esse é o único
          // lugar que mostra a foto, então continua aparecendo.
          <div className={`comprovante-foto ${temLocalizacao ? "tela-only" : ""}`} style={{ marginTop: 20, textAlign: "center" }}>
            <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 10px" }}>
              Selfie no momento da batida
            </p>
            <img className="comprovante-foto-img" src={fotoUrl} alt="Selfie da batida" style={{ maxWidth: 220, borderRadius: RADIUS.md, border: `1px solid ${COLORS.border}` }} />
          </div>
        )}

        <div className="comprovante-codigo" style={{ marginTop: 20, padding: 14, background: COLORS.gray, borderRadius: RADIUS.md }}>
          <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 6px" }}>
            Código de verificação
          </p>
          {dados.hash_verificacao ? (
            <>
              <p className="comprovante-hash" style={{ fontSize: 12.5, color: COLORS.text, margin: 0, fontFamily: "monospace", wordBreak: "break-all", letterSpacing: "0.02em", lineHeight: 1.6 }}>
                {dados.hash_verificacao}
              </p>
              <p className="comprovante-codigo-explicacao" style={{ fontSize: 11, color: COLORS.textLight, margin: "8px 0 0", lineHeight: 1.5 }}>
                Gerado no momento do registro com uma chave exclusiva do servidor PontoZipar, a partir dos dados desta batida (data/hora, tipo, localização, IP e foto, quando houver). Qualquer alteração posterior nesses dados invalida esse código.
              </p>
            </>
          ) : (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>
              Não disponível — registro anterior à criação deste recurso.
            </p>
          )}
        </div>

        <p className="comprovante-footer" style={{ fontSize: 11, color: COLORS.textLight, textAlign: "center", marginTop: 28, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
          Este documento reflete os dados registrados eletronicamente pelo PontoZipar no momento da batida de ponto.
          <br />
          Comprovante emitido em {dataHoraLocal(dados.emitido_em)}.
        </p>
      </Card>
    </div>
  );
}
